import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCommentMedical, faInbox, faReply, faChevronLeft, 
    faCircle, faTrashAlt, faPaperclip, faTimes 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';

const Contact = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [view, setView] = useState('form'); 
    const [messages, setMessages] = useState([]);
    const [status, setStatus] = useState('');
    const [file, setFile] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const goldColor = '#c5a059';
    const darkBlue = '#0f172a';

    const user = useMemo(() => {
        try {
            const saved = localStorage.getItem('liber_user');
            return saved ? JSON.parse(saved) : null;
        } catch (e) { return null; }
    }, []);

    const token = localStorage.getItem('token');

    const [formData, setFormData] = useState({ 
        name: '', email: '', phone: '', subject: '', message: '' 
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchUserMessages = useCallback(async () => {
        if (!user || !user.id || !token) return;
        try {
            const res = await fetch(`${API_BASE}/api/messages/user/${user.id}`, {
                headers: { 
                    
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(Array.isArray(data) ? data : []); 
            }
        } catch (e) { console.error("Error fetching messages", e); }
    }, [user, token]);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.full_name || user.name || '',
                email: user.email || '',
                phone: user.phone || ''
            }));
            fetchUserMessages();
        }
    }, [user, fetchUserMessages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            setStatus(`❌ ${t('contact_login_required')}`);
            return;
        }
        setStatus(`⏳ ${t('contact_sending')}`);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('phone', formData.phone);
        data.append('email', formData.email);
        data.append('subject', formData.subject);
        data.append('message', formData.message);
        data.append('user_id', user?.id || ""); 
        if (file) data.append('attachment', file);

        try {
            const res = await fetch(`${API_BASE}/api/messages/send`, {
                method: 'POST',
                headers: { 
                
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });

            if (res.ok) {
                setStatus(`✅ ${t('contact_success')}`);
                setFormData({ ...formData, subject: '', message: '' });
                setFile(null);
                fetchUserMessages();
            } else {
                setStatus(`❌ ${t('contact_server_error')}`);
            }
        } catch (error) {
            setStatus(`❌ ${t('contact_connection_error')}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('contact_delete_confirm')) || !token) return;
        try {
            const res = await fetch(`${API_BASE}/api/messages/${id}`, {
                method: 'DELETE',
                headers: { 
                   
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) setMessages(prev => prev.filter(m => m.id !== id));
        } catch (e) { alert(t('contact_error_delete')); }
    };

    return (
        <div style={overlayStyle}>
            <div style={modalContainerStyle(isMobile)}>
                <div style={headerStyle}>
                    <button onClick={() => navigate(-1)} style={{...backBtnStyle, color: goldColor}}>
                        <FontAwesomeIcon icon={faChevronLeft} /> {t('contact_back')}
                    </button>
                    <span style={{fontWeight: '900', fontSize: '16px', color: darkBlue}}>{t('contact_main_title')}</span>
                    <button onClick={() => navigate(-1)} style={closeBtnStyle}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {user && (
                    <div style={tabContainer}>
                        <button 
                            style={view === 'form' ? {...activeTab, color: goldColor} : tab} 
                            onClick={() => setView('form')}
                        >
                            <FontAwesomeIcon icon={faCommentMedical} /> {t('contact_tab_write')}
                        </button>
                        <button 
                            style={view === 'history' ? {...activeTab, color: goldColor} : tab} 
                            onClick={() => setView('history')}
                        >
                            <FontAwesomeIcon icon={faInbox} /> {t('contact_tab_inbox')}
                            {messages.some(m => m.reply && !m.seen) && <FontAwesomeIcon icon={faCircle} style={redDotStyle} />}
                        </button>
                    </div>
                )}

                <div style={scrollContentStyle}>
                    <div style={{ padding: '20px' }}>
                        {view === 'form' ? (
                            <form onSubmit={handleSubmit} style={formStyle}>
                                <div style={formHeader}>
                                    <h2 style={titleStyle}>{t('contact_form_title')}</h2>
                                    <div style={{...underline, background: goldColor}}></div>
                                </div>

                                <div style={isMobile ? {display:'flex', flexDirection:'column', gap:'18px'} : inputGroup}>
                                    <div>
                                        <label style={labelStyle}>{t('contact_label_name')} *</label>
                                        <input type="text" style={user ? {...inputStyle, background: '#f8fafc'} : inputStyle} value={formData.name} readOnly={!!user} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>{t('contact_label_phone')} *</label>
                                        <input type="tel" style={inputStyle} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required />
                                    </div>
                                </div>
                                
                                <div>
                                    <label style={labelStyle}>{t('contact_label_subject')}</label>
                                    <input type="text" style={inputStyle} value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} placeholder={t('contact_subject_placeholder')} />
                                </div>

                                <div>
                                    <label style={labelStyle}>{t('contact_label_message')} *</label>
                                    <textarea style={{...inputStyle, height: '120px', resize: 'none'}} required value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} />
                                </div>

                                <div style={{...fileUploadBox, background: 'rgba(197, 160, 89, 0.05)', borderColor: goldColor}}>
                                    <label style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: goldColor, fontWeight: 'bold', justifyContent:'center'}}>
                                        <FontAwesomeIcon icon={faPaperclip} /> 
                                        {file ? `${t('contact_file_selected')}: ${file.name.substring(0, 15)}...` : t('contact_attach_image')}
                                        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} style={{display: 'none'}} />
                                    </label>
                                </div>

                                {status && <div style={statusStyle(status)}>{status}</div>}
                                <button type="submit" style={{...submitBtn, background: goldColor}}>{t('contact_btn_send')}</button>
                            </form>
                        ) : (
                            <div style={historyContainer}>
                                {messages.length === 0 ? (
                                    <p style={{textAlign: 'center', color: '#94a3b8', marginTop: '20px'}}>{t('contact_no_messages')}</p>
                                ) : (
                                    messages.map(msg => (
                                        <div key={msg.id} style={{...messageCard, borderLeft: msg.reply ? `5px solid ${goldColor}` : '5px solid #cbd5e1'}}>
                                            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                                                <div style={msgDate}>{new Date(msg.created_at).toLocaleString(i18n.language === 'hy' ? 'hy-AM' : i18n.language === 'ru' ? 'ru-RU' : 'en-US')}</div>
                                                <button onClick={() => handleDelete(msg.id)} style={deleteBtnStyle}><FontAwesomeIcon icon={faTrashAlt} /></button>
                                            </div>
                                            <div style={{fontWeight: '700', fontSize: '14px', color: darkBlue}}>{msg.subject || t('contact_default_subject')}</div>
                                            <div style={msgText}>{msg.message}</div>
                                            
                                            {msg.reply && (
                                                <div style={replyBox}>
                                                    <div style={{...replyTitle, color: goldColor}}><FontAwesomeIcon icon={faReply} /> {t('contact_reply_from')}</div>
                                                    <div style={replyText}>{msg.reply}</div>
                                                    {msg.reply_attachment && (
                                                        <a href={`${API_BASE}${msg.reply_attachment}`} target="_blank" rel="noreferrer" style={{...attachmentLink, color: goldColor}}>
                                                            <FontAwesomeIcon icon={faPaperclip} /> {t('contact_view_attachment')}
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Styles remain the same below... */}
        </div>
    );
};


// --- STYLES ---
const overlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100dvh', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 };
const modalContainerStyle = (isMobile) => ({ width: isMobile ? '100%' : '550px', height: isMobile ? '100%' : '85vh', background: '#fff', borderRadius: isMobile ? '0' : '28px', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', paddingBottom: isMobile ? '80px' : '0', boxSizing: 'border-box' });
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 20px', borderBottom: '1px solid #f1f5f9', background: '#fff', zIndex: 10 };
const closeBtnStyle = { border: 'none', background: 'none', color: '#cbd5e1', fontSize: '22px', cursor: 'pointer' };
const backBtnStyle = { border: 'none', background: 'none', fontWeight: '800', cursor: 'pointer' };
const tabContainer = { display: 'flex', background: '#f8fafc', padding: '6px', margin: '15px 20px', borderRadius: '16px', border: '1px solid #e2e8f0' };
const tab = { flex: 1, padding: '12px', border: 'none', background: 'none', borderRadius: '12px', color: '#64748b', fontWeight: '700', fontSize: '13px', cursor: 'pointer' };
const activeTab = { ...tab, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' };
const scrollContentStyle = { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '20px' };
const formHeader = { textAlign: 'center', marginBottom: '5px' };
const titleStyle = { fontSize: '20px', fontWeight: '900', color: '#0f172a', margin: 0 };
const underline = { width: '30px', height: '3px', margin: '5px auto 15px', borderRadius: '10px' };
const formStyle = { display: 'flex', flexDirection: 'column', gap: '12px' };
const submitBtn = { width: '100%', padding: '16px', color: '#fff', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '15px', cursor: 'pointer', marginTop: '5px', marginBottom: '60px', boxShadow: '0 8px 15px -3px rgba(197, 160, 89, 0.3)' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f1f5f9', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
const inputGroup = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' };
const labelStyle = { fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '6px', display: 'block', textTransform: 'uppercase' };
const fileUploadBox = { padding: '15px', borderRadius: '14px', border: '2px dashed', textAlign: 'center' };
const messageCard = { background: '#fff', padding: '18px', borderRadius: '20px', border: '1px solid #f1f5f9', marginBottom: '15px' };
const msgDate = { fontSize: '11px', color: '#94a3b8' };
const msgText = { fontSize: '14px', color: '#475569', marginTop: '8px' };
const deleteBtnStyle = { background: 'none', color: '#94a3b8', border: 'none', cursor: 'pointer' };
const replyBox = { marginTop: '12px', padding: '15px', background: '#f8fafc', borderRadius: '15px', borderLeft: '4px solid' };
const replyTitle = { fontSize: '12px', fontWeight: '800' };
const replyText = { fontSize: '14px', color: '#334155' };
const attachmentLink = { display: 'block', marginTop: '8px', fontSize: '12px', textDecoration: 'none', fontWeight: 'bold' };
const redDotStyle = { fontSize: '8px', color: '#ef4444', marginLeft: '5px' };
const statusStyle = (status) => ({ textAlign: 'center', padding: '12px', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold', background: status.includes('✅') ? '#f0fdf4' : '#fef2f2', color: status.includes('✅') ? '#166534' : '#991b1b' });
const historyContainer = { display: 'flex', flexDirection: 'column' };

export default Contact;